import React, { useCallback, useEffect, useState, useRef } from 'react';
import baseURL from '../url';
import './Products.css';
import SwiperCore, { Navigation, Pagination, Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDoubleRight, faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import ProductosLoading from '../ProductosLoading/ProductosLoading';
import { Link as Anchor, useNavigate } from "react-router-dom";
import { getProductMoneda } from '../moneda';
import { getItemOptions, isCustomPrice, isStockNotRequired, getStoredCartItems, serializeCartItems } from '../productStock';
import { guardarSiteConfig, obtenerSiteConfigGuardada } from '../siteConfig';
import ProductMedia, { getFirstProductImage, getFirstProductMedia } from '../ProductMedia/ProductMedia';

SwiperCore.use([Navigation, Pagination, Autoplay]);

function formatOutOfStockLabel(value) {
    return value === 'vendido' ? 'Vendido' : 'Agotado';
}

export default function Products() {
    const navigate = useNavigate();
    const [categorias, setCategorias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fixedCategories, setFixedCategories] = useState(false);
    const categoriasRefs = useRef([]);
    const categoriasInputRef = useRef(null);
    const swiperRef = useRef(null);
    const [productos, setProductos] = useState([]);
    const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('Todo');
    const [outOfStockLabel, setOutOfStockLabel] = useState(() => formatOutOfStockLabel(obtenerSiteConfigGuardada()?.outOfStockLabel));

    const cargarConfiguracion = useCallback(() => {
        fetch(`${baseURL}/configGet.php?ts=${Date.now()}`, {
            method: 'GET',
            cache: 'no-store',
        })
            .then(response => response.json())
            .then(data => {
                if (data.config) {
                    guardarSiteConfig(data.config);
                    setOutOfStockLabel(formatOutOfStockLabel(data.config.outOfStockLabel));
                }
            })
            .catch(error => console.error('Error al cargar configuración de productos:', error));
    }, []);

    const handleClickCategoria = (categoria) => {
        setCategoriaSeleccionada(categoria);
    };

    useEffect(() => {
        cargarProductos();
        cargarCategorias();
        cargarConfiguracion();

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [cargarConfiguracion]);

    const handleScroll = () => {
        if (categoriasInputRef.current) {
            if (window.scrollY > categoriasInputRef.current.offsetTop) {
                setFixedCategories(true);
            } else {
                setFixedCategories(false);
            }
        }
    };


    const cargarProductos = () => {
        fetch(`${baseURL}/productosGet.php`, {
            method: 'GET',
        })
            .then(response => response.json())
            .then(data => {
                setProductos(Array.isArray(data.productos) ? data.productos : []);
                setLoading(false);
            })
            .catch(error => console.error('Error al cargar productos:', error));
    };

    const cargarCategorias = () => {
        fetch(`${baseURL}/categoriasGet.php`, {
            method: 'GET',
        })
            .then(response => response.json())
            .then(data => {
                setCategorias(data.categorias || []);
            })
            .catch(error => console.error('Error al cargar categorías:', error));
    };

    const obtenerImagen = (item) => getFirstProductImage(item) || getFirstProductMedia(item);

    const categoriasConProductos = categorias.filter(categoria =>
        productos?.some(producto => producto?.idCategoria === categoria?.idCategoria)
    );

    const getCategoryLabel = (idCategoria) =>
        categorias.find((categoria) => categoria.idCategoria === idCategoria)?.categoria || '';

    const getProductPath = (item) => `/producto/${item.idProducto}/${item.titulo.replace(/\s+/g, '-')}`;

    const getAvailableStock = (item) => {
        if (isStockNotRequired(item)) {
            return Number.POSITIVE_INFINITY;
        }

        const itemOptions = getItemOptions(item || {});
        if (itemOptions.length > 0) {
            return itemOptions.reduce((total, option) => total + Math.max(0, Number(option?.stock) || 0), 0);
        }

        return Math.max(0, Number(item?.stock) || 0);
    };

    const renderPrice = (item) => (
        isCustomPrice(item) ? (
            <h5>Personalizable</h5>
        ) : (
            <>
                <h5> {getProductMoneda(item)} {String(item?.precio)?.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</h5>
                {(item.precioAnterior >= 1 && item.precioAnterior !== undefined) && (
                    <h5 className='precioTachado'>{getProductMoneda(item)} {`${item?.precioAnterior}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</h5>
                )}
            </>
        )
    );

    const addToCartQuick = (event, item) => {
        const itemOptions = getItemOptions(item || {});
        const availableStock = getAvailableStock(item);

        if (availableStock < 1) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        if (itemOptions.length > 1) {
            navigate(getProductPath(item));
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const selectedItem = itemOptions[0]?.label || '';
        const quickAddStock = itemOptions.length > 0
            ? Math.max(0, Number(itemOptions[0]?.stock) || 0)
            : availableStock;

        const cart = getStoredCartItems();
        const existingItemIndex = cart.findIndex((cartItem) =>
            cartItem.idProducto === item.idProducto && cartItem.item === selectedItem
        );
        const quantityInCart = existingItemIndex !== -1 ? cart[existingItemIndex].cantidad : 0;

        if (quantityInCart + 1 > quickAddStock) {
            return;
        }

        if (existingItemIndex !== -1) {
            cart[existingItemIndex] = {
                ...cart[existingItemIndex],
                cantidad: quantityInCart + 1,
            };
        } else {
            cart.push({ idProducto: item.idProducto, item: selectedItem, cantidad: 1 });
        }

        localStorage.setItem('cart', JSON.stringify(serializeCartItems(cart)));
        window.dispatchEvent(new Event('cart-updated'));
    };

    return (
        <div className='ProductsContain'>
            <div className='productsOverviewLinkRow'>
                <Anchor className='productsOverviewLink' to={`/Productos`}>
                    Ver todos los productos <FontAwesomeIcon icon={faAngleDoubleRight} />
                </Anchor>
            </div>
            {productos?.length > 0 && (
                <div className={`categoriasInputs ${fixedCategories ? 'fixed' : ''}`} ref={categoriasInputRef}>
                    <input
                        type="button"
                        value="Todo"
                        onClick={() => handleClickCategoria('Todo')}
                        style={{
                            backgroundColor: categoriaSeleccionada === 'Todo' ? 'var(--color1)' : '',
                            color: categoriaSeleccionada === 'Todo' ? '#fff' : '',
                            borderBottom: categoriaSeleccionada === 'Todo' ? '2px solid var(--color1)' : 'none'
                        }}
                    />
                    {categoriasConProductos.map(({ categoria, idCategoria }) => (
                        <input
                            key={idCategoria}
                            type="button"
                            value={categoria}
                            onClick={() => handleClickCategoria(idCategoria)}
                            style={{
                                backgroundColor: categoriaSeleccionada === idCategoria ? 'var(--color1)' : '',
                                color: categoriaSeleccionada === idCategoria ? '#fff' : '',
                                borderBottom: categoriaSeleccionada === idCategoria ? '2px solid var(--color1)' : 'none'
                            }}
                        />
                    ))}
                </div>
            )}


            {loading ? (
                <ProductosLoading />
            ) : (
                <div className='Products'>
                    {categoriaSeleccionada === 'Todo' && (
                        <>
                            {productos?.some(item => item.masVendido === "si") && (
                                <div className='categoriSection'>
                                    <Swiper
                                        effect={'coverflow'}
                                        grabCursor={true}
                                        slidesPerView={'auto'}
                                        id='swiper_container_products'
                                        navigation={true}
                                        watchOverflow={true}
                                    >
                                        {productos?.filter(item => item.masVendido === "si").map(item => {
                                            const isOutOfStock = getAvailableStock(item) < 1;

                                            return (
                                                <SwiperSlide key={item.idProducto} id='SwiperSlide-scroll-products-masvendidos'>
                                                    <Anchor className='cardProdcutmasVendido' to={getProductPath(item)}>
                                                        <ProductMedia src={obtenerImagen(item)} alt={item.titulo} className="productMediaCard" />
                                                        <h6 className='masVendido'>Más Vendido</h6>
                                                        <div className='cardText'>
                                                            <h4>{item.titulo}</h4>
                                                            <div className='cardMetaText'>
                                                                {item.marca && <span>{item.marca}</span>}
                                                                {getCategoryLabel(item.idCategoria) && <span>{getCategoryLabel(item.idCategoria)}</span>}
                                                            </div>
                                                            <div className='cardFooterRow'>
                                                                <div className='deFLexPrice'>
                                                                    {renderPrice(item)}
                                                                </div>
                                                                <div className='cardActionStack'>
                                                                    {isOutOfStock && <span className='outOfStockLabel'>{outOfStockLabel}</span>}
                                                                    <button
                                                                        type='button'
                                                                        className='quickAddButton'
                                                                        onClick={(event) => addToCartQuick(event, item)}
                                                                        aria-label={`Agregar ${item.titulo} al carrito`}
                                                                    >
                                                                        <FontAwesomeIcon icon={faShoppingCart} />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Anchor>
                                                </SwiperSlide>
                                            );
                                        })}
                                    </Swiper>
                                </div>
                            )}

                            {categoriasConProductos?.map(({ categoria, idCategoria }) => (
                                <div key={idCategoria} className='categoriSection' ref={ref => categoriasRefs.current[categorias.findIndex(cat => cat.idCategoria === idCategoria)] = ref}>
                                    <div className='deFlexTitlesection'>
                                        <h3>{categoria}</h3>
                                        <button onClick={() => handleClickCategoria(idCategoria)}>
                                            Ver más
                                        </button>
                                    </div>

                                    <Swiper
                                        grabCursor={true}
                                        slidesPerView={1}
                                        spaceBetween={12}
                                        navigation={true}
                                        watchOverflow={true}
                                        id='swiper_container_products'
                                        className='categoryProductsSwiper'
                                        breakpoints={{
                                            700: {
                                                slidesPerView: 2,
                                                spaceBetween: 16,
                                            },
                                            1024: {
                                                slidesPerView: 3,
                                                spaceBetween: 20,
                                            },
                                        }}
                                    >


                                        {productos?.filter(item => item.idCategoria === idCategoria).map(item => {
                                            const isOutOfStock = getAvailableStock(item) < 1;

                                            return (
                                                <SwiperSlide className='categoryProductsSlide' key={item.idProducto}>
                                                    <Anchor className='cardProdcut' key={item.idProducto} to={getProductPath(item)}>
                                                        <ProductMedia src={obtenerImagen(item)} alt={item.titulo} className="productMediaCard" />
                                                        <div className='cardText'>
                                                            <h4>{item.titulo}</h4>
                                                            <div className='cardMetaText'>
                                                                {item.marca && <span>{item.marca}</span>}
                                                                {getCategoryLabel(item.idCategoria) && <span>{getCategoryLabel(item.idCategoria)}</span>}
                                                            </div>
                                                            <div className='cardFooterRow'>
                                                                <div className='deFLexPrice'>
                                                                    {renderPrice(item)}
                                                                </div>
                                                                <div className='cardActionButtons'>
                                                                    <div className='cardActionStack'>
                                                                        {isOutOfStock && <span className='outOfStockLabel'>{outOfStockLabel}</span>}
                                                                        <button
                                                                            type='button'
                                                                            className='quickAddButton'
                                                                            onClick={(event) => addToCartQuick(event, item)}
                                                                            aria-label={`Agregar ${item.titulo} al carrito`}
                                                                        >
                                                                            <FontAwesomeIcon icon={faShoppingCart} />
                                                                        </button>
                                                                    </div>
                                                                    <FontAwesomeIcon icon={faAngleDoubleRight} className='iconCard' />
                                                                </div>
                                                            </div>
                                                        </div>

                                                    </Anchor>
                                                </SwiperSlide>
                                            );
                                        })}

                                    </Swiper>
                                </div>

                            ))}
                        </>
                    )}

                    <div className='categoriSectionSelected'>
                        {productos
                            ?.filter(item => categoriaSeleccionada !== 'Todo' && item.idCategoria === categoriaSeleccionada)
                            ?.map(item => {
                                const isOutOfStock = getAvailableStock(item) < 1;

                                return (
                                    <Anchor key={item.idProducto} to={getProductPath(item)}>
                                        <div className='cardProdcutSelected'>
                                            <ProductMedia src={obtenerImagen(item)} alt={item.titulo} className="productMediaCard" />
                                            <div className='cardTextSelected'>
                                                <h4>{item.titulo}</h4>
                                                <div className='cardMetaText'>
                                                    {item.marca && <span>{item.marca}</span>}
                                                    {getCategoryLabel(item.idCategoria) && <span>{getCategoryLabel(item.idCategoria)}</span>}
                                                </div>
                                                <div className='cardFooterRow'>
                                                    <div className='deFLexPrice'>
                                                        {renderPrice(item)}
                                                    </div>
                                                    <div className='cardActionButtons'>
                                                        <div className='cardActionStack'>
                                                            {isOutOfStock && <span className='outOfStockLabel'>{outOfStockLabel}</span>}
                                                            <button
                                                                type='button'
                                                                className='quickAddButton'
                                                                onClick={(event) => addToCartQuick(event, item)}
                                                                aria-label={`Agregar ${item.titulo} al carrito`}
                                                            >
                                                                <FontAwesomeIcon icon={faShoppingCart} />
                                                            </button>
                                                        </div>
                                                        <FontAwesomeIcon icon={faAngleDoubleRight} className='iconCard' />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Anchor>
                                );
                            })}
                    </div>
                </div>
            )}
        </div>
    );
}
